import {
  createSend as defaultCreate,
  deleteSend as defaultDelete,
  downloadSendFile as defaultDownload,
  listSends as defaultList,
} from '../api/sendApi'
import {
  decryptSendFile,
  decryptSendPayload,
  encodeLinkKey,
  encryptSendFile,
  encryptSendPayload,
  generateLinkKey,
  generatePassSalt,
  pbkdf2DerivePassKey,
  sealLinkKey,
  sealSendKeyWithPassKey,
  unsealLinkKey,
  unsealSendKeyWithPassKey,
} from './sendCipher'
import { base64ToBytes } from '@/crypto/encoding'
import type { SendEncryptedResponse, SendItem, SendPayload } from '../types'

/**
 * Send オーケストレータ (T-D-Send)。
 *
 * 設計方針:
 *   - deps 注入で spec 側を network なしでテスト可能にする。
 *   - 復号失敗は個別 null として扱い failureCount に集計する (一件壊れても他が見える)。
 *   - receiveUrl はクライアント側で組み立てる。URL フラグメントに linkKey を埋め込むため
 *     サーバに保存しない (ゼロ知識設計)。
 */

export interface SendFlowDeps {
  list: typeof defaultList
  create: typeof defaultCreate
  delete: typeof defaultDelete
  download: typeof defaultDownload
}

const defaultDeps = (): SendFlowDeps => ({
  list: defaultList,
  create: defaultCreate,
  delete: defaultDelete,
  download: defaultDownload,
})

const buildReceiveUrl = (id: string, encodedLinkKey: string): string =>
  encodedLinkKey
    ? `${window.location.origin}/#send/view/${id}/${encodedLinkKey}`
    : `${window.location.origin}/#send/view/${id}`

const decryptOne = async (
  symmetricKey: Uint8Array,
  resp: SendEncryptedResponse,
): Promise<SendItem | null> => {
  try {
    const linkKey = await unsealLinkKey(symmetricKey, resp.encryptedLinkKey, resp.linkKeyIv)
    const payload = await decryptSendPayload(linkKey, resp.cipher)
    const encodedKey = encodeLinkKey(linkKey)

    return {
      id: resp.id,
      payload,
      encryptedLinkKey: resp.encryptedLinkKey,
      linkKeyIv: resp.linkKeyIv,
      accessCount: resp.accessCount,
      maxAccess: resp.maxAccess,
      expiresAt: resp.expiresAt,
      receiveUrl: buildReceiveUrl(resp.id, encodedKey),
      fileSize: resp.fileSize,
      createdAt: resp.createdAt,
      updatedAt: resp.updatedAt,
    }
  } catch {
    return null
  }
}

/** GET /send → 全件復号 → 復号成功アイテム + failureCount を返す。 */
export const loadSendList = async (
  symmetricKey: Uint8Array,
  deps: SendFlowDeps = defaultDeps(),
): Promise<{ items: readonly SendItem[]; failureCount: number }> => {
  const responses = await deps.list()
  const results = await Promise.all(responses.map(r => decryptOne(symmetricKey, r)))
  const items = results.filter((r): r is SendItem => r !== null)

  return { items, failureCount: results.length - items.length }
}

export interface CreateSendOptions {
  password?: string
  passwordHint?: string
}

/**
 * linkKey を生成 → payload 暗号化 → SK で linkKey を wrap → POST /send → SendItem を返す。
 *
 * options.password が指定された場合 (§4.8.2):
 *   1. passSalt を random 生成
 *   2. passKey = PBKDF2-HMAC-SHA256(password, passSalt, c=250_000)
 *   3. protectedSendKey = AES-GCM(passKey).seal(linkKey)
 *   4. receiveUrl からは linkKey を省略 (パスワード入力で復元)
 */
export const createSendEncrypted = async (
  symmetricKey: Uint8Array,
  payload: SendPayload,
  maxAccess: number,
  expiresAt: string | null,
  options: CreateSendOptions = {},
  deps: SendFlowDeps = defaultDeps(),
): Promise<SendItem> => {
  const linkKey = generateLinkKey()
  const cipher = await encryptSendPayload(linkKey, payload)
  const { encryptedLinkKey, linkKeyIv } = await sealLinkKey(symmetricKey, linkKey)

  let passwordFields: {
    hasPassword: boolean
    passSalt?: string
    protectedSendKey?: string
    pskIv?: string
    passwordHint?: string
  } = { hasPassword: false }

  if (options.password) {
    const passSaltB64 = generatePassSalt()
    const passKey = await pbkdf2DerivePassKey(options.password, base64ToBytes(passSaltB64))
    const { protectedSendKey, pskIv } = await sealSendKeyWithPassKey(passKey, linkKey)

    passwordFields = {
      hasPassword: true,
      passSalt: passSaltB64,
      protectedSendKey,
      pskIv,
      passwordHint: options.passwordHint,
    }
  }

  const resp = await deps.create({
    cipher,
    encryptedLinkKey,
    linkKeyIv,
    maxAccess,
    expiresAt,
    ...passwordFields,
  })

  // パスワードなし: linkKey を URL フラグメントに埋め込む
  // パスワードあり: URL にキーを含めない (受信者がパスワード入力して復元)
  const encodedKey = options.password ? '' : encodeLinkKey(linkKey)

  return {
    id: resp.id,
    payload,
    encryptedLinkKey: resp.encryptedLinkKey,
    linkKeyIv: resp.linkKeyIv,
    accessCount: resp.accessCount,
    maxAccess: resp.maxAccess,
    expiresAt: resp.expiresAt,
    receiveUrl: buildReceiveUrl(resp.id, encodedKey),
    fileSize: resp.fileSize,
    createdAt: resp.createdAt,
    updatedAt: resp.updatedAt,
  }
}

// ── ファイル送信 (Phase 4 §4.3) ──────────────────────────────────

export interface CreateSendFileInput {
  file: File
  /** UI 入力の表示名。空なら File.name を使用。 */
  name: string
  maxAccess: number
  expiresAt: string | null
  options?: CreateSendOptions
}

/**
 * file Send 用 orchestrator。
 *
 * 1. linkKey 生成
 * 2. metadata cipher = AES-GCM(linkKey, JSON{type:'file', name, mimeType, size})
 * 3. file cipher    = AES-GCM(linkKey, fileBytes) → base64(iv || ciphertext || authTag)
 * 4. linkKey を SK で wrap (+ password 保護があれば passKey でも wrap)
 * 5. POST /send で metadata cipher + file cipher を 1 リクエストで送る
 *
 * メモリ使用: File.arrayBuffer() で全 byte を一度メモリに載せる。10 MB 上限なので OK。
 */
export const createSendFileEncrypted = async (
  symmetricKey: Uint8Array,
  input: CreateSendFileInput,
  deps: SendFlowDeps = defaultDeps(),
): Promise<SendItem> => {
  const { file, name, maxAccess, expiresAt, options = {} } = input

  const linkKey = generateLinkKey()
  const buffer = await file.arrayBuffer()
  const fileBytes = new Uint8Array(buffer)

  const displayName = name.trim() || file.name
  const mimeType = file.type || 'application/octet-stream'

  const payload: SendPayload = {
    type: 'file',
    name: displayName,
    mimeType,
    size: file.size,
  }

  const cipher = await encryptSendPayload(linkKey, payload)
  const { fileCiphertext, size } = await encryptSendFile(linkKey, fileBytes)
  const { encryptedLinkKey, linkKeyIv } = await sealLinkKey(symmetricKey, linkKey)

  let passwordFields: {
    hasPassword: boolean
    passSalt?: string
    protectedSendKey?: string
    pskIv?: string
    passwordHint?: string
  } = { hasPassword: false }

  if (options.password) {
    const passSaltB64 = generatePassSalt()
    const passKey = await pbkdf2DerivePassKey(options.password, base64ToBytes(passSaltB64))
    const { protectedSendKey, pskIv } = await sealSendKeyWithPassKey(passKey, linkKey)

    passwordFields = {
      hasPassword: true,
      passSalt: passSaltB64,
      protectedSendKey,
      pskIv,
      passwordHint: options.passwordHint,
    }
  }

  const resp = await deps.create({
    cipher,
    encryptedLinkKey,
    linkKeyIv,
    maxAccess,
    expiresAt,
    fileCiphertext,
    fileSize: size,
    ...passwordFields,
  })

  const encodedKey = options.password ? '' : encodeLinkKey(linkKey)

  return {
    id: resp.id,
    payload,
    encryptedLinkKey: resp.encryptedLinkKey,
    linkKeyIv: resp.linkKeyIv,
    accessCount: resp.accessCount,
    maxAccess: resp.maxAccess,
    expiresAt: resp.expiresAt,
    receiveUrl: buildReceiveUrl(resp.id, encodedKey),
    fileSize: resp.fileSize,
    createdAt: resp.createdAt,
    updatedAt: resp.updatedAt,
  }
}

/**
 * 受信側: 暗号化済 file bytes を取得 → linkKey で復号 → 平文 bytes を返す。
 *
 * 呼出し側 (SendReceivePage) は Blob にして `URL.createObjectURL` で download を発火する。
 *
 * @throws AES-GCM OperationError (改ざん / 鍵違い)
 * @throws Error('send_gone' | 'send_not_found' | 'send_fetch_failed')
 */
export const downloadAndDecryptSendFile = async (
  id: string,
  linkKey: Uint8Array,
  deps: SendFlowDeps = defaultDeps(),
): Promise<Uint8Array> => {
  const encryptedBytes = await deps.download(id)

  return decryptSendFile(linkKey, encryptedBytes)
}

/**
 * パスワード保護された Send の linkKey を受信者側で復元する。
 * passKey = PBKDF2(password, passSalt) → AES-GCM.open(protectedSendKey)
 *
 * @throws AES-GCM OperationError (パスワード間違い / 改ざん)
 */
export const decryptSendKeyWithPassword = async (
  password: string,
  passSalt: string,
  protectedSendKey: string,
  pskIv: string,
): Promise<Uint8Array> => {
  const passKey = await pbkdf2DerivePassKey(password, base64ToBytes(passSalt))

  return unsealSendKeyWithPassKey(passKey, protectedSendKey, pskIv)
}

/** DELETE /send/:id */
export const deleteSendById = (id: string, deps: SendFlowDeps = defaultDeps()): Promise<void> =>
  deps.delete(id)
